import {
  Button,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreatePersonal, useUpdatePersonal } from '@/api/usePersonal';
import { EgGrStufeSelector } from '@/components/EgGrStufeSelector';
import { MoneyInput } from '@/components/MoneyInput';
import type { PersonalRow, Tarifart, UUID } from '@/api/types';
import {
  ausbildungAnteil,
  ausbildungAnteilJahresmittel,
  ausbildungVerlauf,
  isAnerkennungsjahrEg,
  isPivaEg,
} from '@/constants/ausbildung';
import { describeApiError } from './utils';

interface PersonalEditDialogProps {
  opened: boolean;
  planId: UUID;
  position: PersonalRow | null;
  /** Tarifart der Stelle (bei Neuanlage; bei Edit wird ``position.tarifart`` genutzt). */
  tarifart?: Tarifart;
  /** Foerderjahr des Plans — Bezugsjahr fuer den Ausbildungsverlauf. */
  foerderjahr: number;
  onClose: () => void;
  onSaved: () => void;
}

interface FormValues {
  kategorie: string;
  eg_gruppe: string;
  eg_stufe: string;
  std_woche: number;
  fachkraft: boolean;
  /** Fachkraft zur Mitarbeit (HKJGB). Nur bei tarifart='sue' relevant.
   *  Impliziert ``fachkraft=true``. */
  fzm: boolean;
  sue_zulage: boolean;
  manuelles_brutto: number | null;
  /** JSZ-Override als Prozent (z.B. 95). null = nach Tarif. */
  jsz_anteil_override_pct: number | null;
  /** Nur relevant bei tarifart='ausbildung' und eg_gruppe='IV-PivA': true =
   *  vom Land geförderte PivA (Anteil 0/30/70 % je Stufe), false =
   *  berufsbegleitend ohne Förderung (100 %). */
  piva_landesgefoerdert: boolean;
  name: string;
  aktiv_ab_monat: string | null; // Mantine-Select gibt string zurück
  aktiv_bis_monat: string | null;
  /** Ausbildungsbeginn/-ende als Monat und Jahr, jeweils als String aus dem
   *  Select. Zusammengesetzt ergeben sie das ISO-Datum, das gespeichert
   *  wird ('2026-08-01'). */
  ausbildung_start_monat: string | null;
  ausbildung_start_jahr: string | null;
  ausbildung_ende_monat: string | null;
  ausbildung_ende_jahr: string | null;
}

/** '2026-08-01' → { monat: '8', jahr: '2026' } */
function zerlegeDatum(iso: string | null | undefined): {
  monat: string | null;
  jahr: string | null;
} {
  if (!iso) return { monat: null, jahr: null };
  const [jahr, monat] = iso.split('-');
  return { monat: String(Number(monat)), jahr };
}

/** { '8', '2026' } → '2026-08-01'. Unvollständige Angaben ergeben null. */
function fuegeDatumZusammen(
  monat: string | null,
  jahr: string | null,
): string | null {
  if (!monat || !jahr) return null;
  return `${jahr}-${String(monat).padStart(2, '0')}-01`;
}

const MONAT_OPTIONS = [
  { value: '1', label: 'Januar' },
  { value: '2', label: 'Februar' },
  { value: '3', label: 'März' },
  { value: '4', label: 'April' },
  { value: '5', label: 'Mai' },
  { value: '6', label: 'Juni' },
  { value: '7', label: 'Juli' },
  { value: '8', label: 'August' },
  { value: '9', label: 'September' },
  { value: '10', label: 'Oktober' },
  { value: '11', label: 'November' },
  { value: '12', label: 'Dezember' },
];

/**
 * Der Ausbildungsverlauf in einem Satz.
 *
 * "1. Ausbildungsjahr, ab August 2." — die Probe fuer den Nutzer, dass der
 * eingetragene Zeitraum das trifft, was er meint. Monate ausserhalb des
 * Zeitraums bleiben unerwaehnt; laeuft die Ausbildung gar nicht in diesem
 * Jahr, gibt es nichts zu sagen.
 */
function beschreibeVerlauf(
  verlauf: ReadonlyArray<{ monat: number; ausbildungsjahr: number } | null>,
): string | null {
  const aktiv = verlauf.filter(
    (m): m is { monat: number; ausbildungsjahr: number } => m !== null,
  );
  if (aktiv.length === 0) return null;
  const abschnitte: { jahr: number; abMonat: number }[] = [];
  for (const m of aktiv) {
    const letzter = abschnitte[abschnitte.length - 1];
    if (!letzter || letzter.jahr !== m.ausbildungsjahr) {
      abschnitte.push({ jahr: m.ausbildungsjahr, abMonat: m.monat });
    }
  }
  return abschnitte
    .map((a, i) =>
      i === 0
        ? `${a.jahr}. Ausbildungsjahr`
        : `ab ${MONAT_OPTIONS[a.abMonat - 1].label} das ${a.jahr}.`,
    )
    .join(', ');
}

function monatToStr(v: number | null | undefined): string | null {
  return v ? String(v) : null;
}
function monatFromStr(v: string | null): number | null {
  return v ? parseInt(v, 10) : null;
}

export function PersonalEditDialog({
  opened,
  planId,
  position,
  tarifart = 'sue',
  foerderjahr,
  onClose,
  onSaved,
}: PersonalEditDialogProps) {
  const { t } = useTranslation(['personal', 'common', 'errors']);
  const create = useCreatePersonal(planId);
  const update = useUpdatePersonal(planId);
  const isEdit = position !== null;
  const effectiveTarifart: Tarifart = position?.tarifart ?? tarifart;

  const form = useForm<FormValues>({
    initialValues: {
      kategorie: position?.kategorie ?? '',
      eg_gruppe: position?.eg_gruppe ?? '',
      eg_stufe: position?.eg_stufe ?? '',
      std_woche: position?.std_woche ?? 39,
      fachkraft: position?.fachkraft ?? true,
      fzm: position?.fzm ?? false,
      sue_zulage: position?.sue_zulage ?? false,
      manuelles_brutto: position?.manuelles_brutto ?? null,
      jsz_anteil_override_pct:
        position?.jsz_anteil_override != null
          ? Number((position.jsz_anteil_override * 100).toFixed(2))
          : null,
      piva_landesgefoerdert: position?.piva_landesgefoerdert ?? false,
      name: position?.name ?? '',
      aktiv_ab_monat: monatToStr(position?.aktiv_ab_monat ?? null),
      aktiv_bis_monat: monatToStr(position?.aktiv_bis_monat ?? null),
      ausbildung_start_monat: zerlegeDatum(position?.ausbildung_start).monat,
      ausbildung_start_jahr: zerlegeDatum(position?.ausbildung_start).jahr,
      ausbildung_ende_monat: zerlegeDatum(position?.ausbildung_ende).monat,
      ausbildung_ende_jahr: zerlegeDatum(position?.ausbildung_ende).jahr,
    },
    validate: {
      kategorie: (v) =>
        v.trim().length > 0 ? null : t('personal:validation.kategorieRequired'),
      // Eine Stelle braucht entweder eine Eingruppierung oder ein manuelles
      // Brutto — nicht beides. Wer das Gehalt frei eingibt, soll keine
      // Entgeltgruppe erfinden müssen, die ohnehin überschrieben würde und
      // danach fälschlich in der Übersicht stünde.
      eg_gruppe: (v, values) =>
        v || values.manuelles_brutto != null
          ? null
          : t('personal:validation.egOderBrutto'),
      eg_stufe: (v, values) => {
        // Bei Anerkennungsjahr-EGs gibt es keine Stufe; der EG-Selector
        // setzt intern '1' und blendet das Feld aus → Pflicht-Check skip.
        if (
          effectiveTarifart === 'ausbildung' &&
          isAnerkennungsjahrEg(values.eg_gruppe)
        ) {
          return null;
        }
        // Mit Ausbildungsbeginn ergibt sich das Ausbildungsjahr Monat fuer
        // Monat aus dem Zeitraum — eine feste Stufe waere eine zweite,
        // widersprechende Angabe. Das Feld ist dann ausgeblendet.
        if (effectiveTarifart === 'ausbildung' && values.ausbildung_start_jahr) {
          return null;
        }
        // Ohne gewählte Gruppe ist die Stufe gegenstandslos; den Fall
        // "weder noch" meldet bereits das Gruppen-Feld.
        if (!values.eg_gruppe) return null;
        return v ? null : t('personal:validation.stufeRequired');
      },
      std_woche: (v) => (v >= 0 ? null : t('personal:validation.stdWocheRange')),
      ausbildung_ende_jahr: (_v, values) => {
        const start = fuegeDatumZusammen(
          values.ausbildung_start_monat,
          values.ausbildung_start_jahr,
        );
        const ende = fuegeDatumZusammen(
          values.ausbildung_ende_monat,
          values.ausbildung_ende_jahr,
        );
        if (start && ende && ende < start) {
          return t('personal:validation.ausbildungEndeVorStart');
        }
        return null;
      },
      aktiv_bis_monat: (v, values) => {
        const ab = monatFromStr(values.aktiv_ab_monat);
        const bis = monatFromStr(v);
        if (ab !== null && bis !== null && bis < ab) {
          return 'Austrittsmonat muss >= Eintrittsmonat sein';
        }
        return null;
      },
    },
  });

  useEffect(() => {
    if (opened) {
      form.setValues({
        kategorie: position?.kategorie ?? '',
        eg_gruppe: position?.eg_gruppe ?? '',
        eg_stufe: position?.eg_stufe ?? '',
        std_woche: position?.std_woche ?? 39,
        fachkraft: position?.fachkraft ?? true,
        sue_zulage: position?.sue_zulage ?? false,
        manuelles_brutto: position?.manuelles_brutto ?? null,
        jsz_anteil_override_pct:
          position?.jsz_anteil_override != null
            ? Number((position.jsz_anteil_override * 100).toFixed(2))
            : null,
        piva_landesgefoerdert: position?.piva_landesgefoerdert ?? false,
        name: position?.name ?? '',
        aktiv_ab_monat: monatToStr(position?.aktiv_ab_monat ?? null),
        aktiv_bis_monat: monatToStr(position?.aktiv_bis_monat ?? null),
        ausbildung_start_monat: zerlegeDatum(position?.ausbildung_start).monat,
        ausbildung_start_jahr: zerlegeDatum(position?.ausbildung_start).jahr,
        ausbildung_ende_monat: zerlegeDatum(position?.ausbildung_ende).monat,
        ausbildung_ende_jahr: zerlegeDatum(position?.ausbildung_ende).jahr,
      });
      form.resetDirty();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, position]);

  const submit = form.onSubmit(async (values) => {
    try {
      const isVka = effectiveTarifart === 'vka';
      const isAusbildung = effectiveTarifart === 'ausbildung';
      const pct = values.jsz_anteil_override_pct;
      const jszFactor =
        pct == null || Number.isNaN(pct)
          ? null
          : Number((pct / 100).toFixed(4));
      // Bei Anerkennungsjahr-EGs ist die Stufe im UI ausgeblendet — wir
      // schicken intern '1', damit das Backend-Pflichtfeld eg_stufe stets
      // einen plausiblen Wert hat.
      const ausbStart = isAusbildung
        ? fuegeDatumZusammen(
            values.ausbildung_start_monat,
            values.ausbildung_start_jahr,
          )
        : null;
      const ausbEnde = isAusbildung
        ? fuegeDatumZusammen(
            values.ausbildung_ende_monat,
            values.ausbildung_ende_jahr,
          )
        : null;
      // Mit hinterlegtem Zeitraum ergibt sich das Ausbildungsjahr aus dem
      // Kalender. Eine gespeicherte Stufe waere daneben eine zweite Wahrheit
      // — sie wird darum geleert.
      let ausbStufe = values.eg_stufe;
      if (isAusbildung) {
        if (ausbStart) ausbStufe = '';
        else if (isAnerkennungsjahrEg(values.eg_gruppe)) ausbStufe = '1';
      }
      // Leere Auswahl heißt "keine Eingruppierung" — als null senden, nicht
      // als Leerstring. Sonst stünde in der Übersicht eine leere, aber
      // gesetzte Entgeltgruppe, und die Prüfung im Backend ("entweder
      // Eingruppierung oder manuelles Brutto") ließe sich mit '' aushebeln.
      const egOderNull = values.eg_gruppe.trim() === '' ? null : values.eg_gruppe;
      const stufeOderNull =
        egOderNull === null || ausbStufe.trim() === '' ? null : ausbStufe;
      const ausbPivaLand =
        isAusbildung && isPivaEg(values.eg_gruppe)
          ? values.piva_landesgefoerdert
          : null;
      const payload = {
        tarifart: effectiveTarifart,
        kategorie: values.kategorie,
        eg_gruppe: egOderNull,
        eg_stufe: stufeOderNull,
        std_woche: values.std_woche,
        fachkraft: isVka || isAusbildung ? false : values.fachkraft,
        fzm: isVka || isAusbildung ? false : values.fzm,
        sue_zulage: isVka || isAusbildung ? false : values.sue_zulage,
        manuelles_brutto: values.manuelles_brutto,
        jsz_anteil_override: jszFactor,
        // Legacy-Spalte ab Migration 0017 nicht mehr befüllen.
        ausbildung_form: null,
        piva_landesgefoerdert: ausbPivaLand,
        name: values.name.trim() === '' ? null : values.name.trim(),
        aktiv_ab_monat: monatFromStr(values.aktiv_ab_monat),
        aktiv_bis_monat: monatFromStr(values.aktiv_bis_monat),
        ausbildung_start: ausbStart,
        ausbildung_ende: ausbEnde,
      };
      if (isEdit && position) {
        await update.mutateAsync({ id: position.id, patch: payload });
        notifications.show({ color: 'teal', message: t('personal:info.updated') });
      } else {
        await create.mutateAsync(payload);
        notifications.show({ color: 'teal', message: t('personal:info.created') });
      }
      onSaved();
      onClose();
    } catch (err) {
      notifications.show({
        color: 'red',
        title: t('errors:generic'),
        message: describeApiError(err) || t('errors:generic'),
      });
    }
  });

  // Jahre rund um das Foerderjahr: eine Ausbildung, die in diesem Jahr
  // laeuft, hat spaetestens drei Jahre vorher begonnen und endet
  // spaetestens drei Jahre danach.
  const jahrOptions = Array.from({ length: 7 }, (_, i) =>
    String(foerderjahr - 3 + i),
  );

  // Der abgeleitete Verlauf: Fachkraft-Anteil im Jahresmittel und, wenn das
  // Ausbildungsjahr mitten im Foerderjahr wechselt, der Monat des Wechsels.
  // Das ist die Probe fuer den Nutzer, dass der Zeitraum richtig sitzt.
  const zeitraum = {
    eg_gruppe: form.values.eg_gruppe || null,
    eg_stufe: form.values.eg_stufe || null,
    piva_landesgefoerdert: form.values.piva_landesgefoerdert,
    ausbildung_start: fuegeDatumZusammen(
      form.values.ausbildung_start_monat,
      form.values.ausbildung_start_jahr,
    ),
    ausbildung_ende: fuegeDatumZusammen(
      form.values.ausbildung_ende_monat,
      form.values.ausbildung_ende_jahr,
    ),
  };
  const hatZeitraum = zeitraum.ausbildung_start != null;
  const anteilText = hatZeitraum
    ? `${Math.round(ausbildungAnteilJahresmittel(zeitraum, foerderjahr) * 100)} %`
    : `${Math.round(
        ausbildungAnteil(
          form.values.eg_gruppe,
          form.values.eg_stufe,
          form.values.piva_landesgefoerdert,
        ) * 100,
      )} %`;
  const verlaufText = hatZeitraum
    ? beschreibeVerlauf(ausbildungVerlauf(zeitraum, foerderjahr))
    : null;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={isEdit ? t('personal:form.edit.title') : t('personal:form.create.title')}
      centered
      size="lg"
    >
      <form onSubmit={submit}>
        <Stack>
          <TextInput
            label={t('personal:stelle.kategorie')}
            placeholder={
              effectiveTarifart === 'vka'
                ? t('personal:stelle.kategoriePlaceholder.vka')
                : effectiveTarifart === 'ausbildung'
                  ? t('personal:stelle.kategoriePlaceholder.ausbildung')
                  : t('personal:stelle.kategoriePlaceholder')
            }
            required
            {...form.getInputProps('kategorie')}
          />
          <TextInput label={t('personal:stelle.name')} {...form.getInputProps('name')} />
          <EgGrStufeSelector
            tarifart={effectiveTarifart}
            stufeVerborgen={hatZeitraum}
            value={{
              eg: form.values.eg_gruppe || null,
              stufe: form.values.eg_stufe || null,
            }}
            onChange={(v) => {
              form.setFieldValue('eg_gruppe', v.eg ?? '');
              form.setFieldValue('eg_stufe', v.stufe ?? '');
            }}
          />
          <NumberInput
            label={t('personal:stelle.stdWoche')}
            min={0}
            max={60}
            step={0.5}
            decimalScale={2}
            {...form.getInputProps('std_woche')}
          />
          {effectiveTarifart === 'sue' ? (
            <>
              <Group grow>
                <Switch
                  label={t('personal:stelle.fachkraft')}
                  checked={form.values.fachkraft}
                  onChange={(e) => {
                    const v = e.currentTarget.checked;
                    form.setFieldValue('fachkraft', v);
                    // Fachkraft ausgeschaltet → FzM macht keinen Sinn mehr.
                    if (!v) form.setFieldValue('fzm', false);
                  }}
                />
                <Switch
                  label={t('personal:stelle.sueZulage')}
                  {...form.getInputProps('sue_zulage', { type: 'checkbox' })}
                />
              </Group>
              <Switch
                label={t('personal:stelle.fzm')}
                description={t('personal:stelle.fzmHint')}
                checked={form.values.fzm}
                onChange={(e) => {
                  const v = e.currentTarget.checked;
                  form.setFieldValue('fzm', v);
                  // FzM angeschaltet → Fachkraft automatisch true.
                  if (v) form.setFieldValue('fachkraft', true);
                }}
              />
            </>
          ) : null}
          {effectiveTarifart === 'ausbildung' && isPivaEg(form.values.eg_gruppe) ? (
            <Switch
              label={t('personal:stelle.pivaLandesgefoerdert')}
              description={t('personal:stelle.pivaLandesgefoerdertHint')}
              checked={form.values.piva_landesgefoerdert}
              onChange={(e) =>
                form.setFieldValue('piva_landesgefoerdert', e.currentTarget.checked)
              }
            />
          ) : null}
          {effectiveTarifart === 'ausbildung' ? (
            <>
              <Group grow>
                <Select
                  label={t('personal:stelle.ausbildungStart')}
                  description={t('personal:stelle.ausbildungStartHint')}
                  data={MONAT_OPTIONS}
                  clearable
                  {...form.getInputProps('ausbildung_start_monat')}
                />
                <Select
                  label={t('personal:stelle.ausbildungStartJahr')}
                  // Beide Jahr-Felder heissen sichtbar "Jahr"; erst der
                  // Vorlese-Name sagt, zu welchem Datum sie gehoeren.
                  aria-label={t('personal:stelle.ausbildungStartJahrAria')}
                  data={jahrOptions}
                  clearable
                  {...form.getInputProps('ausbildung_start_jahr')}
                />
              </Group>
              <Group grow>
                <Select
                  label={t('personal:stelle.ausbildungEnde')}
                  description={t('personal:stelle.ausbildungEndeHint')}
                  data={MONAT_OPTIONS}
                  clearable
                  {...form.getInputProps('ausbildung_ende_monat')}
                />
                <Select
                  label={t('personal:stelle.ausbildungEndeJahr')}
                  aria-label={t('personal:stelle.ausbildungEndeJahrAria')}
                  data={jahrOptions}
                  clearable
                  {...form.getInputProps('ausbildung_ende_jahr')}
                />
              </Group>
              <Text size="sm" c="dimmed" data-testid="ausbildung-verlauf">
                {t('personal:stelle.ausbildungAnteilInfo')}
                {': '}
                <Text component="span" fw={600}>
                  {anteilText}
                </Text>
                {verlaufText ? (
                  <>
                    {' — '}
                    {verlaufText}
                  </>
                ) : null}
              </Text>
            </>
          ) : null}
          {/* Bei hinterlegtem Ausbildungszeitraum bestimmt dieser die
              aktiven Monate. Die Felder stuenden sonst da und haetten
              keine Wirkung. */}
          {hatZeitraum ? null : (
            <Group grow>
              <Select
                label="Aktiv ab Monat"
                description="leer = ab Januar"
                data={MONAT_OPTIONS}
                clearable
                {...form.getInputProps('aktiv_ab_monat')}
              />
              <Select
                label="Aktiv bis Monat"
                description="leer = bis Dezember"
                data={MONAT_OPTIONS}
                clearable
                {...form.getInputProps('aktiv_bis_monat')}
              />
            </Group>
          )}
          <MoneyInput
            label={t('personal:stelle.manuellesBrutto')}
            description={t('personal:stelle.manuellesBruttoHint')}
            value={form.values.manuelles_brutto}
            onChange={(v) => form.setFieldValue('manuelles_brutto', v)}
          />
          <NumberInput
            label={t('personal:stelle.jszOverride')}
            description={t('personal:stelle.jszOverrideHint')}
            placeholder={t('personal:stelle.jszOverridePlaceholder')}
            min={0}
            max={200}
            step={1}
            decimalScale={2}
            suffix="%"
            {...form.getInputProps('jsz_anteil_override_pct')}
          />
          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={onClose}>
              {t('common:actions.cancel')}
            </Button>
            <Button type="submit" loading={create.isPending || update.isPending}>
              {t('common:actions.save')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
